import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Zap, Users, Shield, User, Check, Star, Brain, Code, Image, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PLANS } from "@/config/pricing";

const Index = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    checkAuth();

    // Auto-rotate tabs
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % 3);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setIsAuthenticated(!!session);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
  };

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: ["50 prompts/month", "Basic refinements", "Community support", "Standard response time"],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Pro",
      price: "$19",
      period: "per month",
      features: ["500 prompts/month", "Advanced refinements", "Priority support", "API access", "Custom templates"],
      cta: "Start Pro Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "per month",
      features: ["Unlimited prompts", "Custom solutions", "Dedicated support", "SLA guarantees", "Team management"],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const useCases = [
    {
      icon: <Image className="w-6 h-6" />,
      title: "Image Generation",
      description: "Perfect prompts for Midjourney, DALL-E, and Stable Diffusion",
      active: activeTab === 0
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Video Creation",
      description: "Optimize prompts for Gen-2, Runway, and other AI video tools",
      active: activeTab === 1
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Code Generation",
      description: "Enhanced prompts for Copilot, ChatGPT, and Claude",
      active: activeTab === 2
    }
  ];

  return (
    <div className="min-h-screen bg-background dark:bg-background">
      <header className="py-6 px-4 sm:px-6 lg:px-8 border-b border-border">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="text-2xl font-bold text-foreground">PromptPerfect</div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Button 
                  variant="ghost" 
                  className="text-foreground hover:text-foreground/80"
                  onClick={() => navigate('/profile')}
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
                <Button 
                  variant="ghost" 
                  className="text-foreground hover:text-foreground/80"
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard
                </Button>
                <Button 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login" className="text-foreground hover:text-foreground/80">Login</Link>
                  </Button>
                <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link to="/signup">Get Started</Link>
                  </Button>
              </>
            )}
          </div>
        </nav>
      </header>

      <main>
      {/* Hero Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-6xl font-bold text-foreground mb-6">
            Transform Ideas Into
            <br />
            Perfect Prompts
          </h1>
            <p className="text-xl text-muted-foreground mb-8">
            Turn your raw thoughts into optimized prompts for AI image generation, video creation, app development, and more. Powered by advanced AI refinement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Button 
                size="lg" 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            ) : (
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3 text-lg">
                  Start Refining Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
            )}
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10 px-8 py-3 text-lg">
              See Examples
            </Button>
          </div>
        </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Perfect Prompts for Every Need</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: useCase.active ? 1 : 0.7,
                  y: 0,
                  scale: useCase.active ? 1.05 : 1
                }}
                transition={{ duration: 0.5 }}
                  className={`bg-background dark:bg-background/50 rounded-2xl p-8 shadow-lg transition-all duration-300 cursor-pointer
                    ${useCase.active ? 'border-2 border-primary' : 'hover:shadow-xl hover:-translate-y-2'}`}
                onClick={() => setActiveTab(index)}
              >
                  <div className="w-12 h-12 bg-gradient-to-r from-primary to-pink-500 rounded-xl flex items-center justify-center mb-6">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{useCase.title}</h3>
                  <p className="text-muted-foreground">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/50 dark:bg-muted/20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
            Why Choose PromptPerfect?
          </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 rounded-lg bg-background dark:bg-background/50 border border-border">
                <div className="w-12 h-12 bg-gradient-to-r from-primary to-pink-500 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-primary" />
              </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Lightning Fast</h3>
                <p className="text-muted-foreground leading-relaxed">
                Get professionally refined prompts in seconds. Our AI understands context and optimizes for your specific use case.
              </p>
              </div>
              <div className="p-6 rounded-lg bg-background dark:bg-background/50 border border-border">
                <div className="w-12 h-12 bg-gradient-to-r from-primary to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Multi-Purpose</h3>
                <p className="text-muted-foreground leading-relaxed">
                Perfect for image generation, video prompts, app development briefs, and creative writing. One tool for all your needs.
              </p>
              </div>
              <div className="p-6 rounded-lg bg-background dark:bg-background/50 border border-border">
                <div className="w-12 h-12 bg-gradient-to-r from-primary to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Secure & Private</h3>
                <p className="text-muted-foreground leading-relaxed">
                Your prompts and ideas are stored securely. Full account management with history tracking and privacy controls.
              </p>
            </div>
          </div>
        </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-muted/50">
          <div className="container px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">Simple, Transparent Pricing</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Choose the plan that's right for you. All plans include a 14-day money-back guarantee.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {PLANS.map((plan) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`relative bg-card rounded-xl shadow-lg overflow-hidden ${
                    plan.popular ? "border-2 border-primary" : "border border-border"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-primary px-3 py-1 rounded-bl-lg">
                      <span className="text-sm font-medium text-primary-foreground">Popular</span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-foreground">{plan.name}</h3>
                    <div className="mt-4 flex items-baseline">
                      <span className="text-4xl font-bold text-foreground">${plan.price}</span>
                      <span className="ml-1 text-muted-foreground">/{plan.interval}</span>
                    </div>
                    <p className="mt-2 text-muted-foreground">{plan.description}</p>

                    <Button
                      className="w-full mt-6"
                      variant={plan.popular ? "default" : "outline"}
                      asChild
                    >
                      <Link to="/signup">
                        {plan.price === 0 ? "Get Started" : "Try Pro Free"}
                      </Link>
                    </Button>

                    <div className="mt-8 space-y-4">
                      {plan.features.map((feature) => (
                        <div key={feature.name} className="flex items-start">
                          <Check className="h-5 w-5 text-primary shrink-0" />
                          <div className="ml-3">
                            <p className="text-foreground">{feature.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {feature.description}
                              {feature.limit && ` (${feature.limit} per month)`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Loved by Creators Worldwide</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="bg-background dark:bg-background/50 rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                </div>
                  <p className="text-muted-foreground mb-6">
                  "PromptPerfect has revolutionized my workflow. The refined prompts consistently produce better results, saving me hours of trial and error."
                </p>
                <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-cyan-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold">Creative Professional</h4>
                      <p className="text-muted-foreground text-sm">AI Artist</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ready to Perfect Your Prompts?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of creators who trust PromptPerfect for their AI workflows
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 dark:bg-primary/90 dark:hover:bg-primary/80">
              <Link to="/signup">Sign Up Now</Link>
              </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
